import axios from "axios";

const useSubscribeFormBlock = (blockElement: HTMLElement): void => {
    const formElement = blockElement.querySelector(".subscribe-form-block__form") as HTMLFormElement;
    const emailInputElement = blockElement.querySelector('.subscribe-form-block__email-input') as HTMLInputElement;
    const consentInputElement = blockElement.querySelector('.subscribe-form-block__consent-checkbox') as HTMLInputElement;
    const subscribeUrl: string | undefined = formElement.dataset.subscribeUrl;
    const responseMessageElement = formElement.querySelector('.subscribe-form-block__response-message') as HTMLParagraphElement;
    
    const validOnSubmit = (event: SubmitEvent): boolean => {
        let isEmailInputValid = isInputValid(emailInputElement, event);
        let isConsentInputValid = isInputValid(consentInputElement, event);
        
        if (!isEmailInputValid || !isConsentInputValid) {
            return false;
        } else {
            return true;
        }
    }
    
    const isInputValid = (inputElement: HTMLInputElement, event: Event): boolean => {
        let inputContainerElement: HTMLDivElement | null = null;
        let inputContainerClass: string | null = null;
        let errorMessageElement: HTMLParagraphElement | null = null;
        
        if (inputElement.type === 'email') {
            inputContainerElement = inputElement.closest('.input-text') as HTMLDivElement;
            inputContainerClass = "input-text";
            errorMessageElement = inputElement.previousElementSibling as HTMLParagraphElement;
        } else {
            inputContainerElement = inputElement.closest('.checkbox') as HTMLDivElement;
            inputContainerClass = "checkbox";
            errorMessageElement = inputElement.parentElement?.previousElementSibling as HTMLParagraphElement;
        }
        
        if (event instanceof SubmitEvent) {
            if (inputElement.checkValidity()) {
                setFormFieldAsValid(inputElement, inputContainerElement, inputContainerClass, errorMessageElement);
                
                return true;
            } else {
                setFormFieldAsInvalid(inputElement, inputContainerElement, inputContainerClass, errorMessageElement);
                setFocusToFirstInvalidInput();
                
                return false;
            }
        } else {
            if (inputContainerElement.classList.contains(`${inputContainerClass}--valid`) || inputContainerElement.classList.contains(`${inputContainerClass}--invalid`)) {
                if (inputElement.checkValidity()) {
                    setFormFieldAsValid(inputElement, inputContainerElement, inputContainerClass, errorMessageElement);

                    return true;
                } else {
                    setFormFieldAsInvalid(inputElement, inputContainerElement, inputContainerClass, errorMessageElement);

                    return false;
                }
            } else {
                if (inputElement.checkValidity()) {
                    setFormFieldAsValid(inputElement, inputContainerElement, inputContainerClass, errorMessageElement);
                    return true;
                } else {
                    return false;
                }
            }
        }
    }
    
    const setFormFieldAsValid = (
        inputElement: HTMLInputElement,
        inputContainerElement: HTMLDivElement,
        inputContainerClass: string,
        errorMessageElement: HTMLParagraphElement
    ): void => {
        if (inputElement.type === 'email') {
            inputElement.classList.add('input-text__input--with-icon');
        }
        
        inputContainerElement.classList.add(`${inputContainerClass}--valid`);

        if (inputContainerElement.classList.contains(`${inputContainerClass}--invalid`)) {
            inputContainerElement.classList.remove(`${inputContainerClass}--invalid`);
        }

        if (inputElement.hasAttribute('aria-invalid')) {
            inputElement.removeAttribute('aria-invalid');
        }

        errorMessageElement.innerText = "";
        errorMessageElement.hidden = true;
    }
    
    const setFormFieldAsInvalid = (
        inputElement: HTMLInputElement,
        inputContainerElement: HTMLDivElement,
        inputContainerClass: string,
        errorMessageElement: HTMLParagraphElement
    ): void => {
        const errorMessage = inputElement.dataset.validationMessage as string;

        if (inputElement.type === 'email') {
            inputElement.classList.add('input-text__input--with-icon');
        }

        inputContainerElement.classList.add(`${inputContainerClass}--invalid`);
        inputElement.setAttribute('aria-invalid', 'true');
        inputElement.setAttribute('aria-describedby', `${errorMessageElement.id}`);

        if (inputContainerElement.classList.contains(`${inputContainerClass}--valid`)) {
            inputContainerElement.classList.remove(`${inputContainerClass}--valid`);
        }

        errorMessageElement.innerText = errorMessage;
        errorMessageElement.hidden = false;
    }
    
    const clearFormInputsOnSuccess = (): void => {
        const emailInputContainerElement = emailInputElement.closest('.input-text') as HTMLDivElement;
        const consentInputContainerElement = consentInputElement.closest('.checkbox') as HTMLDivElement;
        
        emailInputElement.value = "";
        emailInputContainerElement.classList.remove('input-text--valid');
        
        consentInputElement.checked = false;
        consentInputContainerElement.classList.remove('checkbox--valid');
    }
    
    const setFocusToFirstInvalidInput = () => {
        const invalidInputElements = Array.from(formElement.querySelectorAll('[class*=\'--invalid\'] input')) as HTMLInputElement[];
        
        if (invalidInputElements.length > 0) {
            const firstElement = invalidInputElements[0];
            firstElement.focus();
        }
    }
    
    const onSubmit = ():void => {
        if (subscribeUrl !== undefined) {
            axios.post(subscribeUrl, { email: emailInputElement.value }, {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded"
                }
            })
                .then((response) => {
                    responseMessageElement.textContent = response.data.message;
                    responseMessageElement.hidden = false;
    
                    if (response.data.success !== null) {
                        if (response.data.success) {
                            responseMessageElement.classList.add("subscribe-form-block__response-message--success");
                            clearFormInputsOnSuccess();
                        } else {
                            responseMessageElement.classList.add("subscribe-form-block__response-message--error");
                        }
                    } else {
                        responseMessageElement.classList.add("subscribe-form-block__response-message--error");
                    }
                })
                .catch((error: Error) => {
                    responseMessageElement.classList.add("subscribe-form-block__response-message--error");
                });
        }
    }

    /* Initialize and add event listeners */
    const initialize = (): void => {
        emailInputElement.addEventListener('input', (event: Event) => {
            if (!isInputValid(emailInputElement, event)) {
                return;
            }
        });

        emailInputElement.addEventListener('blur', (event: Event) => {
            if (!isInputValid(emailInputElement, event)) {
                return;
            }
        });
        
        consentInputElement.addEventListener('input', (event: Event) => {
            if (!isInputValid(consentInputElement, event)) {
                return;
            }
        });
        
        consentInputElement.addEventListener('blur', (event: Event) => {
            if (!isInputValid(consentInputElement, event)) {
                return;
            }
        });
        
        formElement.addEventListener('submit', (event: SubmitEvent) => {
            event.preventDefault();

            if (!validOnSubmit(event)) {
                return;
            }
            
            onSubmit();
        });
    };

    initialize();
};

document.addEventListener('DOMContentLoaded', () => {
    const subscribeFormBlockElements = document.querySelectorAll('.subscribe-form-block') as NodeListOf<HTMLDivElement>;

    if (subscribeFormBlockElements.length > 0) {
        subscribeFormBlockElements.forEach((blockElement) => {
            useSubscribeFormBlock(blockElement);
        })
    }
});
